import { Pre } from '@ijoin/components';
import {
  Alert,
  AlertTitle,
  LinearProgress,
  Stack,
  styled,
  Typography
} from '@mui/material';

import { Component, ErrorInfo, FC, ReactNode, Suspense } from 'react';

interface AxiosLikeError extends Error {
  config?: {
    baseURL?: string;
    data?: unknown;
    headers?: Record<string, unknown>;
    method?: string;
    params?: unknown;
    url?: string;
  };
  isAxiosError?: boolean;
  response?: {
    data?: unknown;
    headers?: Record<string, unknown>;
    status?: number;
    statusText?: string;
  };
}

const isAxiosError = (error: unknown): error is AxiosLikeError =>
  error instanceof Error && (error as AxiosLikeError).isAxiosError === true;

const formatBody = (value: unknown) => {
  if (value == null) return '(empty)';
  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const ErrorPre = styled(Pre)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  maxHeight: 240,
  padding: theme.spacing(1)
}));

const AxiosErrorDetails: FC<{ error: AxiosLikeError }> = props => {
  const method = props.error.config?.method?.toUpperCase() ?? 'UNKNOWN';
  const url = props.error.config?.url ?? '(no url)';
  const status = props.error.response?.status ?? '(no response)';
  const statusText = props.error.response?.statusText ?? '';

  return (
    <Stack spacing={1.5}>
      <Typography variant='subtitle2'>
        {method} {url} → {status} {statusText}
      </Typography>

      <Stack spacing={0.5}>
        <Typography color='text.secondary' variant='caption'>
          Request
        </Typography>
        <ErrorPre>{formatBody(props.error.config?.data)}</ErrorPre>
      </Stack>

      <Stack spacing={0.5}>
        <Typography color='text.secondary' variant='caption'>
          Response
        </Typography>
        <ErrorPre>{formatBody(props.error.response?.data)}</ErrorPre>
      </Stack>
    </Stack>
  );
};

const StandardErrorDetails: FC<{ error: Error }> = props => (
  <Stack spacing={1.5}>
    <Typography variant='subtitle2'>{props.error.message}</Typography>

    {props.error.stack && (
      <Stack spacing={0.5}>
        <Typography color='text.secondary' variant='caption'>
          Stack
        </Typography>
        <ErrorPre>{props.error.stack}</ErrorPre>
      </Stack>
    )}
  </Stack>
);

const ErrorFallback: FC<{ error: Error }> = props => (
  <Stack
    alignItems='center'
    justifyContent='center'
    sx={{ flex: 1, minHeight: '60vh', p: 2 }}>
    <Alert severity='error' sx={{ maxWidth: 720, width: '100%' }}>
      <AlertTitle>
        {isAxiosError(props.error) ? 'Request failed' : 'Something went wrong'}
      </AlertTitle>
      {isAxiosError(props.error) ? (
        <AxiosErrorDetails error={props.error} />
      ) : (
        <StandardErrorDetails error={props.error} />
      )}
    </Alert>
  </Stack>
);

interface ErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error?: Error;
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { error, hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (!this.state.hasError || !this.state.error) return this.props.children;

    return <ErrorFallback error={this.state.error} />;
  }
}

export const QueryBoundary: FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = props => (
  <Stack sx={{ flex: 1, minHeight: 0 }}>
    <ErrorBoundary>
      <Suspense fallback={props.fallback ?? <LinearProgress />}>
        {props.children}
      </Suspense>
    </ErrorBoundary>
  </Stack>
);

QueryBoundary.displayName = 'QueryBoundary';
